import { useEffect, useRef } from 'react';

const RELEVANT_EVENTS = new Set([
  'order_created',
  'order_updated',
  'order_cancelled',
  'order_paid',
  'order_round_added',
  'order_replay',
  'table_session_updated',
]);

interface UseWaiterOrderStreamArgs {
  restaurantUuid: string;
  token: string;
  enabled: boolean;
  onOrdersChanged: () => void;
}

interface SseEvent {
  id: string | null;
  event: string;
}

function saleBaseUrl(): string {
  return (import.meta.env.VITE_SALE_API_URL ?? 'http://localhost:8003').replace(/\/+$/, '');
}

function parseSseEvent(raw: string): SseEvent {
  let id: string | null = null;
  let event = 'message';
  for (const line of raw.split(/\r?\n/)) {
    if (line.startsWith('id:')) {
      id = line.slice(3).trim();
    } else if (line.startsWith('event:')) {
      event = line.slice(6).trim();
    }
  }
  return { id, event };
}

function appendLastEventId(url: string, lastEventId: string | null): string {
  if (!lastEventId) return url;
  const u = new URL(url);
  u.searchParams.set('last_event_id', lastEventId);
  return u.toString();
}

/**
 * DF-0153 — reculs de reconnexion, et minuterie d'inactivite du flux.
 *
 * Mesure prod du 2026-09-04 (journal Traefik, 24 h, routeur des tablettes
 * serveur) : 295 (re)connexions au flux pour 13 restaurants, dont **231 ont
 * vecu moins de 10 s** ; 2 seulement ont depasse 10 min. Chez Yuji, 182
 * connexions dans la journee. Chaque reconnexion rejoue la fenetre de 12 h puis
 * declenche 4 invalidations de cache, soit ~5 requetes et une authentification
 * de plus.
 *
 * Trois causes, trois correctifs :
 *
 * 1. le delai etait **fixe a 3 s**, succes comme erreur : pendant un
 *    redemarrage de sale_api (~60 s), N tablettes retapent toutes les 3 s en
 *    cadence. Recul exponentiel avec gigue, comme `sseOrderStream` cote owner ;
 * 2. un `401/403` (jeton revoque) n'etait pas distingue d'une panne — la boucle
 *    ne s'arretait que lorsque le sondage `tables/` effacait l'authentification
 *    (SC-381). On s'arrete maintenant tout de suite et on laisse le sondage
 *    conclure ;
 * 3. `reader.read()` n'avait aucune minuterie. Le serveur emet un `heartbeat`
 *    toutes les 10 s mais le client ne le regardait pas : apres un changement
 *    de borne WiFi ou une mise en veille (TCP demi-ouvert, cas normal d'une
 *    tablette en salle), le flux restait un zombie jusqu'a ce que le noyau
 *    abandonne ses retransmissions — 15 min ou plus. Les tableaux de bord ont
 *    ce chien de garde depuis toujours ; la tablette, non.
 */
const RECONNECT_MIN_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
/** Trois battements manques. Le serveur en emet un toutes les 10 s. */
const STREAM_IDLE_TIMEOUT_MS = 30_000;

function nextReconnectDelay(previous: number): number {
  return Math.min(previous * 2, RECONNECT_MAX_MS);
}

/** Gigue pleine : un delai uniforme dans [0, plafond].
 *
 * Sans elle, N tablettes deconnectees au meme instant se reconnectent toutes
 * exactement a 1 s / 2 s / 4 s, en vagues synchronisees sur le rejeu.
 */
function jitter(ms: number): number {
  return Math.random() * ms;
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

export function useWaiterOrderStream({
  restaurantUuid,
  token,
  enabled,
  onOrdersChanged,
}: UseWaiterOrderStreamArgs) {
  const onOrdersChangedRef = useRef(onOrdersChanged);
  const refreshQueuedRef = useRef(false);

  useEffect(() => {
    onOrdersChangedRef.current = onOrdersChanged;
  }, [onOrdersChanged]);

  useEffect(() => {
    if (!enabled || !restaurantUuid || !token) return;

    const controller = new AbortController();
    let lastEventId: string | null = null;
    let refreshTimer: number | null = null;

    const queueRefresh = () => {
      if (refreshQueuedRef.current) return;
      refreshQueuedRef.current = true;
      refreshTimer = window.setTimeout(() => {
        refreshQueuedRef.current = false;
        refreshTimer = null;
        onOrdersChangedRef.current();
      }, 0);
    };

    const handleChunk = (chunk: string, buffer: { value: string }) => {
      buffer.value += chunk;
      const parts = buffer.value.split(/\r?\n\r?\n/);
      buffer.value = parts.pop() ?? '';
      for (const part of parts) {
        if (!part.trim() || part.startsWith(':')) continue;
        const parsed = parseSseEvent(part);
        if (parsed.id) lastEventId = parsed.id;
        if (RELEVANT_EVENTS.has(parsed.event)) queueRefresh();
      }
    };

    const run = async () => {
      const base = `${saleBaseUrl()}/sale/order/order/stream/?restaurant_uuid=${encodeURIComponent(restaurantUuid)}`;
      let reconnectDelay = RECONNECT_MIN_MS;
      while (!controller.signal.aborted) {
        try {
          const response = await fetch(appendLastEventId(base, lastEventId), {
            method: 'GET',
            headers: {
              Accept: 'text/event-stream',
              Authorization: `Bearer ${token}`,
            },
            credentials: 'include',
            signal: controller.signal,
          });
          // Jeton révoqué : réessayer ne peut pas aider, et la boucle tapait
          // jusqu'à ce que le sondage `tables/` efface l'authentification.
          if (response.status === 401 || response.status === 403) return;
          if (!response.ok || !response.body) {
            throw new Error(`waiter stream failed: ${response.status}`);
          }
          // Le flux tient : le prochain incident repart du délai le plus court.
          reconnectDelay = RECONNECT_MIN_MS;

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          const buffer = { value: '' };

          while (!controller.signal.aborted) {
            // Sans cette minuterie, un flux zombie (TCP demi-ouvert après un
            // changement de borne WiFi, cas normal d'une tablette en salle)
            // restait ouvert jusqu'à ce que le noyau abandonne ses
            // retransmissions — 15 min ou plus — et la tablette ne voyait plus
            // rien arriver alors qu'elle se croyait connectée. Annuler le
            // lecteur fait résoudre le `read()` en cours avec `done`, ce qui
            // sort d'ici et laisse la boucle externe reconnecter.
            const idleTimer = window.setTimeout(() => {
              void reader.cancel().catch(() => {});
            }, STREAM_IDLE_TIMEOUT_MS);
            let done: boolean;
            let value: Uint8Array | undefined;
            try {
              ({ done, value } = await reader.read());
            } finally {
              window.clearTimeout(idleTimer);
            }
            if (done) break;
            handleChunk(decoder.decode(value, { stream: true }), buffer);
          }
          const tail = decoder.decode();
          if (tail) handleChunk(tail, buffer);
        } catch {
          if (controller.signal.aborted) return;
        }
        await delay(jitter(reconnectDelay), controller.signal);
        reconnectDelay = nextReconnectDelay(reconnectDelay);
      }
    };

    void run();
    return () => {
      controller.abort();
      if (refreshTimer !== null) {
        window.clearTimeout(refreshTimer);
        refreshQueuedRef.current = false;
      }
    };
  }, [enabled, restaurantUuid, token]);
}
